import * as FC from "@fluentui/react-components";
import { EQUIPMENT_REMINDER_SCOPE_ACTIONABLE, formatDateOnly } from "../../services/equipment_service";

const useStyles = FC.makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("6px"),
    },
    cards: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
        ...FC.shorthands.gap("10px"),
    },
    card: {
        cursor: "pointer",
        textAlign: "left",
        ...FC.shorthands.padding("12px"),
        ...FC.shorthands.border("1px", "solid", FC.tokens.colorNeutralStroke2),
        ...FC.shorthands.borderRadius(FC.tokens.borderRadiusMedium),
        backgroundColor: FC.tokens.colorNeutralBackground1,
    },
    activeCard: {
        ...FC.shorthands.border("2px", "solid", FC.tokens.colorBrandStroke1),
    },
    value: {
        fontSize: "24px",
        fontWeight: 600,
        marginTop: "4px",
    },
    caption: {
        color: FC.tokens.colorNeutralForeground3,
    },
});

/**
 * The card counts and the list must always agree.
 *
 * The three date cards are counted by the backend over the actionable set only
 * (未归档 且 available/in_use), so they carry `reminderScope=actionable`. 维修/保养中 is a plain
 * status counter and must NOT carry that scope, because `actionable` excludes `under_service`
 * by definition. 器材总数 counts every non-archived record, so it clears all filters.
 */
export const equipmentReminderCardFilters = {
    total: {},
    maintenanceOverdue: {
        maintenanceState: "overdue",
        reminderScope: EQUIPMENT_REMINDER_SCOPE_ACTIONABLE,
    },
    maintenanceDueSoon: {
        maintenanceState: "due_soon",
        reminderScope: EQUIPMENT_REMINDER_SCOPE_ACTIONABLE,
    },
    warrantyDueSoon: {
        warrantyState: "due_soon",
        reminderScope: EQUIPMENT_REMINDER_SCOPE_ACTIONABLE,
    },
    underService: {
        operationalStatus: "under_service",
    },
};

/**
 * @param {object} props
 * @param {object} props.summary GET /equipment/reminders/summary response
 * @param {string} props.activeKey currently applied card key
 * @param {(key: string, filter: object) => void} props.onSelect
 */
export const ReminderSummaryCards = ({ summary, activeKey, onSelect }) => {
    const styles = useStyles();
    // Thresholds come from the summary response, never hardcoded in the UI.
    const maintenanceDays = summary?.maintenanceDueSoonDays;
    const warrantyDays = summary?.warrantyDueSoonDays;

    const cards = [
        { key: "total", label: "器材总数", value: summary?.total },
        { key: "maintenanceOverdue", label: "保养逾期", value: summary?.maintenanceOverdue },
        {
            key: "maintenanceDueSoon",
            label: maintenanceDays ? `${maintenanceDays} 天内需保养` : "即将需要保养",
            value: summary?.maintenanceDueSoon,
        },
        {
            key: "warrantyDueSoon",
            label: warrantyDays ? `${warrantyDays} 天内保修到期` : "保修即将到期",
            value: summary?.warrantyDueSoon,
        },
        { key: "underService", label: "维修/保养中", value: summary?.underService },
    ];

    return (
        <div className={styles.root}>
            <div className={styles.cards}>
                {cards.map((card) => (
                    <div
                        key={card.key}
                        role="button"
                        tabIndex={0}
                        className={FC.mergeClasses(
                            styles.card,
                            activeKey === card.key && styles.activeCard
                        )}
                        onClick={() => onSelect?.(card.key, equipmentReminderCardFilters[card.key])}
                        onKeyDown={(evt) => {
                            if (evt.key === "Enter" || evt.key === " ") {
                                evt.preventDefault();
                                onSelect?.(card.key, equipmentReminderCardFilters[card.key]);
                            }
                        }}
                    >
                        <FC.Caption1>{card.label}</FC.Caption1>
                        <div className={styles.value}>
                            {card.value === undefined || card.value === null ? "-" : card.value}
                        </div>
                    </div>
                ))}
            </div>
            {summary?.businessDate && (
                <FC.Caption1 className={styles.caption}>
                    {`业务日期（Asia/Shanghai）：${formatDateOnly(summary.businessDate)}；提醒卡统计范围为未归档且可用/使用中的器材。`}
                </FC.Caption1>
            )}
        </div>
    );
};
